import {
    Raycaster,
    Vector2,
    Vector3
} from 'da-three';
import EventDispatcher from './../service/EventDispatcher.js';
import Highlight from './../model/Highlight.js';

Math.clamp = function (val, min, max) {
    return (val >= min && val <= max) ? val : (val > max) ? max : min;
};

export default class HighlightManager { 
    constructor(mediator,config,controls,camera,sceneMan,renderMan, animMan) {
        this._camera = camera;
        this._controls = controls;
        this._config = config;
        this.mediator = mediator;
        this.sceneMan = sceneMan;
        this.renderMan = renderMan;
        this.animMan = animMan;
        this._domElement = this._controls.domElement;
        
        this.highlights = [];
        this.clickable = [];
        this.visibility = [];
        this.highlightsOn = this._config.features.showHighlight.default;
        
        this.startPos = new Vector2();
        this.modes = { ORBIT: 0, TO_HIGHLIGHT: 1, HIGHLIGHT: 2, TO_ORBIT: 3 };
        this.state = this.modes.ORBIT;
        this.lastTime = Date.now();
        this.posTarget = new Vector3();
        this.viewTarget = new Vector3(); /**/ this.viewCurrent = new Vector3();
        this.fovTarget = 90;
        this.baseFov = this._camera.fov;

        // PARAMS
        this.inDelay = false; 

        this.segueOnAnimation = true;
        this.lastFov = 90;
        this.dragThreshold = 0.01;
        this.EPS = 0.000001;
        this.inAnimateSpeed = 3;
        this.outAnimateSpeed = 6;
        this.FOVAnimateSpeed = 6;

        this.__dbg_hl = null;

        this.onTouchStart = this.onTouchStart.bind(this);
        this.onMouseStart = this.onMouseStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onMouseEnd = this.onMouseEnd.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseWheel = this.onMouseWheel.bind(this);
        this.mouseWheelFired = false;
        this.mouseWheelEventEnabled = false;

        this.dispatcher = new EventDispatcher();
        this.dispatcher.setEventTypes([
            "highlightClicked",
            "highlightTriggered",
            "onstate",
            "visibilityChange",
            "animationDelay"
        ]);

        this.mediator.addEventListener('afterRender',() => this.update());
        this.mediator.addEventListener('objectComplete', this.onObjectComplete.bind(this));
        this.mediator.addEventListener('animationHook', this.onAnimationHook.bind(this));
        this.mediator.addEventListener('animationState', this.onAnimationState.bind(this));
        document.addEventListener('touchstart', (event) => { 
            if (event.touches && event.touches.length > 0) {
                let touch = event.touches[0];
                this.onDocumentMouseDown(touch);
            }
        });
        document.addEventListener('mousedown', this.onDocumentMouseDown.bind(this), false);
        document.addEventListener('mousemove', this.onDocumentMouseMove.bind(this), false);
    }
    
    
    addHighlights(){
        // if (this._config.tech.debug) console.log('DEBUG ===== Adding highlights...'); // TODO verbosity
        let hLength = this._config.highlights.length;
        if (hLength > 0){
            for (let i=0;i<hLength;i++){
                let highData = this._config.highlights[i];
                this.highlights.push(new Highlight(highData, this._config.project.basedir+this._config.project.static, this.sceneMan.group));
                const hl = this.highlights[i];

                // animation hooks
                if (hl.animation && !hl.isTrigger) {
                    this.animMan.addTimelineHook("highlight", hl.animation.targetTime, hl.animation.timeUnit, hl);
                }

                // visibility
                if (hl.visibility) {
                    this.visibility.push(hl);
                }
                this.clickable.push(this.highlights[i].glow);
            }
        }
        this.toggleClickzones(this.highlightsOn);
        //console.log(this.highlights);
    }
    
    onAnimationHook(event, data) {
        //console.log("Animationhook", data);
        if ((data.animating) && data.key === 'highlight' && this.segueOnAnimation && !data.teleported) {
            const hl = data.payload;
            this.setPOI(hl);
            this.dispatcher.dispatch('highlightClicked', { hl });
            // check for delay!
            if (hl.animation && hl.animation.delay) {
                const delayTime = Date.now();
                this.animMan.animationToggle(false);
                this.inDelay = true;
                this.dispatcher.dispatch("animationDelay", { inDelay: true, start: delayTime, duration: hl.animation.delay });
                setTimeout(() => {
                   
                    this.dispatcher.dispatch("animationDelay", { inDelay: false, start: delayTime, duration: hl.animation.delay }); 
                    if (this.inDelay) {
                        this.animMan.animationToggle(true);
                    }

                    this.inDelay = false;
                    
                }, hl.animation.delay * 1000);
            }
        }
    }

    onAnimationState(event, state) {
        const visible = {};
        let changes = 0;
        this.visibility.forEach(elem => {
            // prepare once for performance reasons
            if (elem.visibility.duration !== state.duration) {
                const unit = elem.animation ? elem.animation.timeUnit : "s";
                if (elem.visibility.to !== undefined && elem.visibility.realTo === undefined) {
                    elem.visibility.realTo = this.animMan.getRealTime({ unit, time: elem.visibility.to }, state.duration);
                }

                if (elem.visibility.from !== undefined && elem.visibility.realFrom === undefined) {
                    elem.visibility.realFrom = this.animMan.getRealTime({ unit, time: elem.visibility.from }, state.duration);
                }
                elem.visibility.duration = state.duration;
            }
            
            const oldVisibility = elem.glow.visible;
            elem.glow.visible =
                    (elem.visibility.whenPlaying ? true : !state.playing && !this.inDelay) // disable if playing is set and animation is not playing
                    && (elem.visibility.realFrom !== undefined ? state.time >= elem.visibility.realFrom : true) // check lower bound
                && (elem.visibility.realTo !== undefined ? state.time <= elem.visibility.realTo : true); // check upper bound
            
            if (oldVisibility !== elem.glow.visible) {
                changes++;
            }

            if (elem.glow.visible) {
                visible[elem.id] = elem;
            }
            
        });

        if (changes > 0 || state.forcedUpdate) {
            this.dispatcher.dispatch('visibilityChange', visible);
        }

        // check mounted highlights
        this.highlights.forEach(hl => {
            if(hl.mount.element) {
                hl.updateMountedPosition();
            }
        });
    }

    setTriggerVisibility(visible) {
        console.log(`${visible?'Show': 'Hide'} all Trigger Highlihgts`);
    }
    
    addListeners(key) {
        this.write("Listener: Add " + key);
        var scope = this;

        switch (key) {
            case "start":
                scope._domElement.addEventListener("mousedown", scope.onMouseStart, false);    
                scope._domElement.addEventListener("touchstart", scope.onTouchStart, false);
                break;
            case "move":
                scope._domElement.addEventListener("touchmove", scope.onTouchMove, false);
                scope._domElement.addEventListener("mousemove", scope.onMouseMove, false);
                break;
            case "end":
                scope._domElement.addEventListener("touchend", scope.onTouchEnd, false);
                scope._domElement.addEventListener("mouseup", scope.onMouseEnd, false);
                break;
            case "wheel":
                scope._domElement.addEventListener("wheel", scope.onMouseWheel, false);
                scope.mouseWheelFired = false;
                scope.mouseWheelEventEnabled = true;
                break;
            default:
                break;
        }
    }
    
    

    capture(h) {
        if (h === undefined) {
            if (this.__dbg_hl !== null) {
                h = this.__dbg_hl;
            } else 
                return;
        }
        var scope = this;
        var cam = scope._camera.position.clone();
        var tar = new Vector3(0, 0, -1).applyQuaternion(scope._camera.quaternion).multiplyScalar(10).add(cam);
        h.setCameraTarget(cam);
        h.setViewTarget(tar);
        h.setFov(scope._camera.fov);
        return h;
    }
    
    
    
    checkIntersect(event){
        let vector = new Vector3( ( event.clientX / window.innerWidth ) * 2 - 1, - ( event.clientY / window.innerHeight ) * 2 + 1, 0.5 );

        vector.unproject( this._camera );
        let raycaster = new Raycaster( this._camera.position, vector.sub( this._camera.position ).normalize() );

        let intersects = raycaster.intersectObjects( this.clickable, true);

        return intersects;
    }



    checkHighLightClickIntersect(event) {
        let intersects = this.checkIntersect(event);

        if (intersects.length > 0 && this.highlightsOn) {
            // if (this._config.tech.debug) console.log('DEBUG ==== Intersect!'); // TODO verbosity

            let cLength = this.clickable.length;
            for (var i = 0; i < cLength; i++) {
                // if (this._config.tech.debug) console.log('DEBUG ===== Intersect: ' + intersects[0].object.id + ' vs. ' + this.clickable[i].id); // TODO verbosity
                if (intersects[0].object.id === this.clickable[i].id) {
                    let hl = this.highlights[i];
                    
                    this.dispatchHighlightClick(hl);
                    break;
                }
            }
        }
    }

    dispatchHighlightClick(hl, immideate = false, fromStart = false) {
        // check if highlight is trigger 
        if (hl.isTrigger || (hl.animation && hl.animation.targetTime && immideate)) {
            if (hl.animation) {
                if (!immideate) {
                    //console.log(hl.animation.targetTime,this.animMan.currentAnimationTime);
                    this.animMan.animateTo({
                        time: hl.animation.targetTime,
                        unit: hl.animation.timeUnit,
                        teleport: hl.animation.teleport || fromStart || hl.animation.targetTime < this.animMan.currentAnimationTime
                    }, (data) => {
                        console.log("Stopped Animation At", data);
                    });
                } else {
                    this.animMan.animateTo({
                        time: hl.animation.targetTime,
                        unit: hl.animation.timeUnit,
                        teleport: true
                    }, (data) => {
                        console.log("Stopped Animation At", data);
                    });
                    this.setPOI(hl);
                    this.dispatcher.dispatch('highlightClicked', { hl });
                }
                
            }
            this.dispatcher.dispatch('highlightClicked', { hl });
        } else {
            this.setPOI(hl);
            this.dispatcher.dispatch('highlightClicked', { hl });
        }
    }

    checkHighlightIntersect(event) {
        if (this.highlightsOn) {
            let intersects = this.checkIntersect(event);
            let hits = {};
            if (intersects.length > 0) {
                for (var j = 0; j < intersects.length; j++) {
                    hits[intersects[j].object.id] = true;
                }
            }   

            for (var i = 0; i < this.clickable.length; i++) {
                if (hits[this.clickable[i].id] === true) {
                    this.highlights[i].setHover(true);
                    //this.hoverHighlight(this.highlights[i].id,true);
                } else {
                    this.highlights[i].setHover(false);
                    //this.hoverHighlight(this.highlights[i].id,false);
                }
            }
        }
    }
        
    
    
    getDirection(obj, prefix) {
        var h = window.innerHeight, w = window.innerWidth;
        return new Vector2(obj[prefix + "X"] / w, obj[prefix + "Y"] / h);
    }
    
    
    
    getHighlightByID(id){
        let hLength = this.highlights.length;
        for (let i=0;i<hLength;i++){
            if (this.highlights[i].id === id){
                return this.highlights[i];
            }
        }
        return false;
    }
    
    
    
    handleEnd = () => {
        var scope = this;

        scope.removeListeners("end");
        scope.removeListeners("move");
        scope.addListeners("start");
    }
    
    
    
    hoverHighlight(id,active){
        if (this._config.features.usePostProcessing.active 
            && this.renderMan.postprocesses.edgeglow !== undefined
            && this.renderMan.postprocesses.edgeglow.enabled){
            if (!active){
                // console.log('End hover');  // TODO verbosity
                this.renderMan.postprocesses.edgeglow.selectedObjects = [];
            } else {
                let hl = this.getHighlightByID(id);
                if (hl !== false){
                    // console.log('Hover highlight: '+hl.headline);  // TODO verbosity
                    this.renderMan.postprocesses.edgeglow.selectedObjects = hl.nodes;
                }
            }
        }
    }



    handleMove = (newPos) => {
        var scope = this;
        if (scope.startPos.distanceTo(newPos) > scope.dragThreshold && scope.state !== scope.modes.TO_ORBIT) {
            scope.write("State: TO_ORBIT");
            scope.state = scope.modes.TO_ORBIT;
            scope.dispatcher.dispatch("onstate", scope.state);
            scope.addListeners("wheel");
            scope._controls.position0 = scope._camera.position.clone();
            scope._controls.target0 = scope.viewCurrent.clone();
            scope._controls.target = scope.viewCurrent.clone();
            scope._controls.reset();
            scope._controls.enabled = true;
            if (this.animMan.animationPlaying) {
                this.mediator.dispatch("toggleState", { key: 'animatedHighlights', value: false });
            }
        }
    }
    
    
    
    handleStart = (pos) => {
        var scope = this;
        scope.startPos = pos;
        scope.removeListeners("start");
        scope.addListeners("move");
        scope.addListeners("end");
    }



    handleWheel = () => {
        this.mouseWheelFired = true;
    }
    
    
    
    interpolateTo(current, target, deltaTime, speed) {
        if (speed <= 0)
            return target;

        var dist = target - current;
        if (dist * dist < this.EPS)
            return target;

        var deltaMove = dist * Math.clamp((deltaTime / 1000) * speed, 0, 1);
        return current + deltaMove;
    }
    
    
    
    onDocumentMouseDown(event) {	
        this.checkHighLightClickIntersect(event);
    }


    onDocumentMouseMove(event) {
        this.checkHighlightIntersect(event);
    }


    onMouseEnd = () => {
        this.handleEnd();
    }
    
    
    onMouseMove = (event) => {
        this.handleMove(this.getDirection(event, "client"));
    }
    

    onMouseStart = (event) => {
        this.handleStart(this.getDirection(event, "client"));
    }
    

    onMouseWheel = () => {
        this.handleWheel();
    }
    
    
    onObjectComplete(){
        if (this._config.highlights !== undefined){
            this.addHighlights();
        }
    }
    
    
    onTouchEnd = () =>{
        this.handleEnd();
    }
    
    
    onTouchMove = (event) => {
        this.handleMove(this.getDirection(event.touches[0], "page"));
    }
    
    
    onTouchStart = (event) => {
        switch (event.touches.length) {
            case 1:
                this.handleStart(this.getDirection(event.touches[0], "page"));    
                break;
            case 2:
                if(this.mouseWheelEventEnabled)    
                    this.mouseWheelFired();    
                break;
            default:
                break;
        }  
    }
    
    
    removeListeners(key) { 
        this.write("Listener: Remove " + key);
        var scope = this;

        switch (key) {
            case "start":
                scope._domElement.removeEventListener("touchstart", scope.onTouchStart, false);
                scope._domElement.removeEventListener("mousedown", scope.onMouseStart, false);
                break;
            case "move":
                scope._domElement.removeEventListener("touchmove", scope.onTouchMove, false);
                scope._domElement.removeEventListener("mousemove", scope.onMouseMove, false);
                break;
            case "end":
                scope._domElement.removeEventListener("touchend", scope.onTouchEnd, false);
                scope._domElement.removeEventListener("mouseup", scope.onMouseEnd, false);
                break;
            case "wheel":
                scope._domElement.removeEventListener("wheel", scope.onMouseWheel, false);
                scope.mouseWheelEventEnabled = false;
                break;
            default:
                scope._domElement.removeEventListener("touchstart", scope.onTouchStart, false);
                scope._domElement.removeEventListener("mousedown", scope.onMouseStart, false);
                scope._domElement.removeEventListener("touchmove", scope.onTouchMove, false);
                scope._domElement.removeEventListener("mousemove", scope.onMouseMove, false);
                scope._domElement.removeEventListener("touchend", scope.onTouchEnd, false);
                scope._domElement.removeEventListener("mouseup", scope.onMouseEnd, false);
                scope._domElement.removeEventListener("wheel", scope.onMouseWheel, false); 
                scope.mouseWheelEventEnabled = false;
                break;
        }
    }

    
    setPOI(highlight) {
        console.log("Going to ", highlight.id);
        var scope = this;

        scope.write("State: TO_HIGHLIGHT");
        scope.state = scope.modes.TO_HIGHLIGHT;
        scope.dispatcher.dispatch("onstate", scope.state);
        // TODO check if we are on Point
        scope._controls.enabled = false;
        scope.lastTime = Date.now();
        scope.posTarget = highlight.getCameraTarget();
        scope.viewTarget = highlight.getViewTarget();
        scope.viewCurrent = new Vector3(0, 0, -1).applyQuaternion(scope._camera.quaternion).multiplyScalar(10).add(scope._camera.position.clone());
        scope.lastFov = scope._camera.fov;
        scope.fovTarget = highlight.getFov();
        scope.inAnimateSpeed = highlight.speed.in;
        scope.outAnimateSpeed = highlight.speed.out;
        scope.FOVAnimateSpeed = highlight.speed.fov;
        scope.addListeners("start");

        this.__dbg_hl = highlight;
        // console.log("Moving to:", highlight); // TODO verbosity
    }
    
    
    /**
     * Toggles visibility of the clickable areas (highlights). Visibility will
     * be set to the provided value.
     * 
     * @param {bool} value true, if the highlights should become visible, false 
     * otherwise 
     */
    toggleClickzones(value){
        this.highlightsOn = value;
        let hLength = this.highlights.length;
        for (let i=0;i<hLength;i++){
            // if (this._config.tech.debug) console.log(this.highlights[i]);  // TODO verbosity
            this.highlights[i].clickzone.visible = this.highlightsOn;
            // if (this._config.tech.debug) console.log('DEBUG ===== Hightlight state: ', this.highlights[i].clickzone, this.highlights[i].clickzone.visible);  // TODO verbosity
            let scope = this;
            this.highlights[i].glow.traverse(function(node){
                node.material.opacity = scope.highlightsOn ? 1 : 0;
            });
        }
    }
    
    
    
    triggerHighlight(id, immideate = false){
        let hl = this.getHighlightByID(id);
        if (hl !== false){
            this.dispatchHighlightClick(hl, immideate);
        }
    }
    
    
    
    update(updateCamera) {
        const scope = this;
        const currentTime = Date.now();
        const timeDelta = currentTime - scope.lastTime;

        switch (scope.state) {
            case scope.modes.TO_HIGHLIGHT:
                // camera position
                scope.v3interpolateTo(scope._camera.position, scope.posTarget, timeDelta, scope.inAnimateSpeed);

                // camera view direction
                scope.v3interpolateTo(scope.viewCurrent, scope.viewTarget, timeDelta, scope.inAnimateSpeed);
                scope._camera.lookAt(scope.viewCurrent);

                // camera fov
                scope._camera.fov = scope.interpolateTo(scope._camera.fov, scope.fovTarget, timeDelta, scope.inAnimateSpeed);
                scope._camera.updateProjectionMatrix();
                if (scope._camera.position.distanceTo(scope.posTarget) < scope.EPS &&
                    scope.viewCurrent.distanceTo(scope.viewTarget) < scope.EPS && 
                    Math.abs(scope._camera.fov, scope.fovTarget) < scope.EPS) {
                    scope.write("State: HIGHLIGHT");
                    scope.state = scope.modes.HIGHLIGHT;
                    scope.dispatcher.dispatch("onstate", scope.state);
                }
                break;
            case scope.modes.TO_ORBIT:
                // TODO change, if center is not rotationcenter! // orbitSize, if cameradistance is changing, change this too
                const orgTarget = new Vector3();
                const orbitSize = 6;

                // camera fov
                scope._camera.fov = scope.interpolateTo(scope._camera.fov, scope.baseFov, timeDelta, scope.FOVAnimateSpeed);
                scope._camera.updateProjectionMatrix();

                scope.v3interpolateTo(scope._controls.target, orgTarget, timeDelta, scope.outAnimateSpeed);
                scope._controls.target0.copy(scope._controls.target);
                
                const orbitDist = scope._camera.position.distanceTo(orgTarget);
                if (!scope.mouseWheelFired && Math.abs(orbitDist - orbitSize) > scope.EPS) {
                    const amm = scope.interpolateTo(orbitDist, orbitSize, timeDelta, scope.FOVAnimateSpeed);
                    const dir = scope._camera.position.clone()
                        .sub(orgTarget) //scope._controls.target
                        .normalize()
                        .multiplyScalar(amm)
                    scope._camera.position.copy(dir);
                }

                if (scope.mouseWheelEventEnabled && scope.mouseWheelFired) {
                    scope.removeListeners("wheel");
                }

                const targetDist = scope._controls.target.distanceTo(orgTarget);
                if (Math.abs(orbitDist - orbitSize) < scope.EPS && targetDist < scope.EPS && Math.abs(scope._camera.fov - scope.baseFov) < scope.EPS) {
                    scope.write("State: ORBIT");
                    scope.state = scope.modes.ORBIT;
                    scope.dispatcher.dispatch("onstate", scope.state);
                    scope.renderMan.removeEdgeGlow();
                    scope.removeListeners();
                    
                    scope._controls.target0.copy(orgTarget);
                    scope._controls.target.copy(orgTarget);
                }
                scope._camera.lookAt(scope._controls.target);
                scope._controls.update();
                break;
            case scope.modes.HIGHLIGHT:
                scope._controls.position0 = scope._camera.position.clone();
                scope._controls.target0 = scope.viewTarget.clone();
                scope._controls.target = scope.viewTarget.clone();
                break;
            case scope.modes.ORBIT:
                scope._controls.update();
                break;
            default:
                scope._controls.update();
                break;
        }
        
        scope.lastTime = currentTime;
    }
    
    
    v3interpolateTo(current, target, deltaTime, speed) {
        if (speed <= 0)
            return current.set(target.x, target.y, target.z);

        const dist = target.clone().sub(current);

        if (dist.lengthSq() < this.EPS)
            return current.set(target.x, target.y, target.z);

        const deltaMove = dist.multiplyScalar(Math.clamp((deltaTime / 1000) * speed, 0, 1));
        return current.add(deltaMove);
    }
    

    write(txt) {
        // if(this._config.tech.debug)  // TODO verbosity
            // console.log("HLMNGR: " + txt);
    }  
};


// WEBPACK FOOTER //
// ./src/js/studio/manager/HighlightManager.js